/**
 * Resolves the category-grouped event catalog for a given integration
 * id. Works for any registered inbound provider AND for Stripe (which
 * is on the legacy OAuth path but still has a parallel event catalog
 * we want to surface in the integration page).
 *
 * Pure data — no React, safe to import from server or client.
 */
import { getInboundPresenter } from '@/lib/integrations/inbound/presenters'
import {
  STRIPE_EVENT_METADATA,
  type StripeSupportedEventMetadata,
} from '@/lib/integrations/oauth/stripe/supported-events'

import type { TriggerableCategory } from './inbound-events-section'

const STRIPE_CATEGORIES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'checkout', label: 'Checkout' },
  { id: 'customer', label: 'Customer' },
  { id: 'subscription', label: 'Subscription' },
  { id: 'invoice', label: 'Invoice' },
  { id: 'quote', label: 'Quote' },
  { id: 'payment', label: 'Payment' },
]

function buildStripeCategories(): Array<TriggerableCategory> {
  const buckets = new Map<string, Array<StripeSupportedEventMetadata>>()
  for (const category of STRIPE_CATEGORIES) {
    buckets.set(category.id, [])
  }
  for (const event of STRIPE_EVENT_METADATA) {
    const bucket = buckets.get(event.category)
    if (bucket) {
      bucket.push(event)
    } else {
      buckets.set(event.category, [event])
    }
  }
  return STRIPE_CATEGORIES.map((category) => ({
    id: category.id,
    label: category.label,
    events: (buckets.get(category.id) ?? []).map((event) => ({
      eventType: event.eventType,
      title: event.title,
      description: event.description,
      category: event.category,
    })),
  }))
}

export function buildTriggerableCategoriesForIntegration(
  integrationId: string
): Array<TriggerableCategory> {
  const presenter = getInboundPresenter(integrationId)
  if (presenter) {
    return presenter.categories.map((category) => ({
      id: category.id,
      label: category.label,
      events: presenter.events
        .filter((event) => event.category === category.id)
        .map((event) => ({
          eventType: event.eventType,
          title: event.title,
          description: event.description,
          category: event.category,
          primaryUseCase: event.useCases[0]?.title,
        })),
    }))
  }
  if (integrationId === 'stripe') {
    return buildStripeCategories()
  }
  return []
}
